import React, { useState, useEffect, useId } from 'react';
import { useRef } from 'react';
import { createMouseDragHandler, getPixelRatio } from '../helpers/canvasHelpers';
import { sendEvent, useEventNotification } from '../hooks/events';
import useResizeObserver from '../hooks/resize-observer';
import {
  invMapRenderCoord,
  isInRenderRange,
  renderChartAreas,
  TpChartAreaDefinition,
  TpLogicalRange,
  TpRenderRange,
  TpZoomPanelCoordMapper,
  TpZoomPanInfo,
  TpZoomPaninfoDual,
  generateDualMappers,
  createAreaPositioningCss,
  renderZoomPanWebGL2D,
  renderWebGL2DForDownload,
  renderHoverLabels,
  clipZoomPan,
  calcPan,
  calcZoom,
  convertZoomPan2WebGlRangeDual,
} from './zoomPanCanvasHelpers';
import { TpWidgetContext } from '../dashboard/interfaces';
import { debounce, debounceInComponent } from '../../../components/miscUtils';
import { useComponentId } from '../hooks/misc';
import { TpWebGl2DContext } from '../../../components/webgl-2d/interfaces';
import { CanvasWebGL } from '../../../components/webgl-2d/CanvasWebGL2D';
import {
  TpChartColorSchema,
  TpLassoSelection,
  TpRectangleSelection,
  calcMaxZoomFactor,
  fastDist,
  getDefaultChartColorSchema,
} from './helpers';
import { TpExportFileNameInfo, useExportFilename } from '../hooks/useExportFilename';

const imageBuffer: any = {};

const groupXYZoomPanInfo: { [id: string]: TpZoomPaninfoDual } = {}; // keeps track of zoom & pan info for synchronized charts, as defined by zoomPanXYGroup. In this case, we keep this information in a separate memory object, shared across all charts in that group. We avoid using the store since that would trigger DOM update events, massively slowing down the UI.

interface TpZoomPanDefinition {
  zpLogicalRangeX: TpLogicalRange;
  zpLogicalRangeY: TpLogicalRange;
  zpRenderRangeX: TpRenderRange;
  zpRenderRangeY: TpRenderRange;
  areas: TpChartAreaDefinition[];
  zoomPanXYGroup?: string; // if set, plots that share the same identifier synchronize their zooming & panning
}

export interface TpHoverInfo {
  dataTableId: string; // identifier of the datatable the selected record resides in
  catColumnId?: string; // if applicable, colum to which the item refers to as a categorical value
  itemId: any; // identifier of the selected item
  elemId?: string; // added by zoompan to identify panel where the request was originating from
  secondDimension?: {
    // same, but for an optional secundary hover dimension
    dataTableId: string;
    itemId: string;
  };
  custom?: any;
}

export interface TpHoverRenderContext {
  isActiveChart: boolean;
}

export interface TpHoverRenderInfo {
  area: TpChartAreaDefinition;
  logicPosX: number | null;
  logicPosY: number | null;
  labels: string[];
  customHoverRenderer?: (
    hoverInfo: TpHoverInfo,
    hoverContext: TpHoverRenderContext,
    ctx: CanvasRenderingContext2D,
    mapX: TpZoomPanelCoordMapper,
    mapY: TpZoomPanelCoordMapper,
    pixelRatio: number,
  ) => void;
}

interface TpZoomPanCanvasProps {
  definition: TpZoomPanDefinition;
  chartTitleInfo?: TpExportFileNameInfo;
  getHoverInfo: (mapper: any, logicX: number, logicY: number, renderX: number, renderY: number) => TpHoverInfo | null;
  getHoverRenderInfo?: (hoverInfo: TpHoverInfo, context: TpHoverRenderContext) => TpHoverRenderInfo | null;
  widgetContext: TpWidgetContext;
  onRectangleSelection?: (rect: TpRectangleSelection) => void;
  onLassoSelection?: (rect: TpLassoSelection) => void;
  onItemClicked?: (itemId: string) => void;
  highRes?: boolean;
  maxZoomXAbsolute?: number; // expressed as the maximum number of screen pixel per logical unit
  maxZoomYAbsolute?: number; // expressed as the maximum number of screen pixel per logical unit
  maxZoomXRelative?: number; // expressed as multiple of no zoom
  maxZoomYRelative?: number; // expressed as multiple of no zoom
  colorSchema?: TpChartColorSchema;
}

export function ZoomPanCanvas(props: TpZoomPanCanvasProps) {
  const compId = useComponentId();
  const { getFilename } = useExportFilename();

  const propsRef = useRef<TpZoomPanCanvasProps | null>(null);
  propsRef.current = props;

  const [measuredDims, setMeasuredDims] = useState('');

  const refLayerDraw = useRef<HTMLCanvasElement>(null);
  const refLayerOverlay = useRef<HTMLCanvasElement>(null);

  const hoverInfoRef = useRef<TpHoverInfo | null>(null);
  const hoverItemIdRef = useRef<string | null>(null);

  const webGLContexts = useRef<{ [id: string]: TpWebGl2DContext }>({});

  const elemId = useId();

  const rectDraggingRef = useRef<any>(null);
  const lassoDraggingRef = useRef<any>(null);
  const isPanningRef = useRef(false);

  const canvasDraw = refLayerDraw.current;
  const contextDraw = canvasDraw?.getContext('2d');
  const pixelRatioRef = useRef<number>(1);
  pixelRatioRef.current = getPixelRatio(contextDraw, props.highRes || false);
  const canvasOverlay = refLayerOverlay.current;
  // let contextOverlay = canvasOverlay?.getContext('2d');

  const zoomingActive = props.widgetContext.zoomingActive;
  const zoomingActiveRef = useRef(false);
  zoomingActiveRef.current = zoomingActive;

  const zoomPanXYGroup = props.definition.zoomPanXYGroup;

  const zoomPanInfo = useRef<TpZoomPaninfoDual>({ x: { zoomFactor: 1, pan: 0 }, y: { zoomFactor: 1, pan: 0 } });

  const colorSchema = props.colorSchema || getDefaultChartColorSchema();

  function setZoomPanX(newZoomInfo: TpZoomPanInfo) {
    const theZoomPanInfo = getZoomPanInfo();
    if (theZoomPanInfo) theZoomPanInfo.x = clipZoomPan(newZoomInfo);
  }

  function setZoomPanY(newZoomInfo: TpZoomPanInfo) {
    const theZoomPanInfo = getZoomPanInfo();
    if (theZoomPanInfo) theZoomPanInfo.y = clipZoomPan(newZoomInfo);
  }

  function getZoomPanInfo() {
    if (!zoomPanXYGroup) return zoomPanInfo?.current;
    else {
      // chart is part of a shared zoom & pan group, so we get the info from there
      if (!groupXYZoomPanInfo[zoomPanXYGroup])
        // in case that info is not yet stored, we create it here
        groupXYZoomPanInfo[zoomPanXYGroup] = { x: { zoomFactor: 1, pan: 0 }, y: { zoomFactor: 1, pan: 0 } };
      return groupXYZoomPanInfo[zoomPanXYGroup];
    }
  }

  const notifyZoomPanChanged = debounce(() => {
    if (zoomPanXYGroup)
      // in case we are part of a zoom & pan synchronization group, so we send a message to be picked up by other members. But we do it debounced, so that the other chart updates are not degrading the performance of the user interaction with the chart of choice
      sendEvent(`zoomPanChanged_${zoomPanXYGroup}`, getZoomPanInfo());
  }, 200);

  useEventNotification(
    'ResetZoomPan',
    () => {
      setZoomPanX({ zoomFactor: 1, pan: 0 });
      setZoomPanY({ zoomFactor: 1, pan: 0 });
      renderCanvas();
    },
    [],
  );

  useEventNotification(
    `zoomPanChanged_${zoomPanXYGroup}`,
    (eventId: string, dualZoomPanInfo: any) => {
      setZoomPanX(dualZoomPanInfo.x);
      setZoomPanY(dualZoomPanInfo.y);
      renderCanvas();
    },
    [],
  );

  const downloadBitmap = () => {
    const canvas = refLayerDraw.current;
    if (!canvas) return;
    renderWebGL2DForDownload(
      canvasDraw,
      contextDraw,
      pixelRatioRef.current,
      getZoomPanInfo(),
      propsRef.current!.definition.areas,
      colorSchema,
      webGLContexts.current!,
    );

    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = props.chartTitleInfo ? getFilename(props.chartTitleInfo, 'png') : 'widget.png';
    link.href = url;
    link.click();
  };

  function renderCanvas() {
    const pixelRatio = pixelRatioRef.current;
    const canvasDraw = refLayerDraw.current;
    const contextDraw = canvasDraw?.getContext('2d');
    if (!canvasDraw) return;
    if (!contextDraw) return;
    contextDraw.fillStyle = colorSchema.backColor.toCanvasString();
    contextDraw.fillRect(0, 0, canvasDraw.width, canvasDraw.height);
    renderChartAreas(
      canvasDraw,
      contextDraw,
      pixelRatio,
      getZoomPanInfo(),
      propsRef.current!.definition.areas,
      colorSchema,
      false,
    );
    if (refLayerDraw.current) {
      renderZoomPanWebGL2D(
        getZoomPanInfo(),
        propsRef.current!.definition.areas,
        webGLContexts.current,
        refLayerDraw.current.width,
        refLayerDraw.current.height,
        pixelRatio,
      );
    }
    renderCanvasOverlay();
    if (canvasDraw.height > 0)
      imageBuffer[compId] = contextDraw.getImageData(0, 0, canvasDraw.width, canvasDraw.height);
  }

  const debouncedrenderCanvas = debounceInComponent(renderCanvas, 25);

  function renderCanvasOverlay() {
    const canvasOverlayLoc = refLayerOverlay.current;
    const hoverInfo: TpHoverInfo | null = hoverInfoRef.current;
    if (!canvasOverlayLoc) return;
    const ctx = canvasOverlayLoc.getContext('2d');
    const pixelRatio = pixelRatioRef.current;
    if (!ctx) return;
    ctx.clearRect(0, 0, canvasOverlayLoc.width, canvasOverlayLoc.height);

    renderChartAreas(
      canvasOverlayLoc,
      ctx,
      pixelRatio,
      getZoomPanInfo(),
      propsRef.current!.definition.areas,
      colorSchema,
      true,
    );

    if (hoverInfo && propsRef.current!.getHoverRenderInfo && !rectDraggingRef.current && !lassoDraggingRef.current) {
      const hoverContext = {
        isActiveChart: hoverInfo.elemId == elemId,
      };
      const hoverRenderInfo = propsRef.current!.getHoverRenderInfo(hoverInfo, hoverContext);

      if (hoverRenderInfo) {
        const area = hoverRenderInfo.area;
        const { mapX, mapY } = generateDualMappers(
          0,
          canvasOverlayLoc.width,
          0,
          canvasOverlayLoc.height,
          area,
          getZoomPanInfo(),
          pixelRatio,
        );
        const hoverCenterX = hoverRenderInfo.logicPosX !== null ? mapX.map(hoverRenderInfo.logicPosX) : null;
        const hoverCenterY = hoverRenderInfo.logicPosY !== null ? mapY.map(hoverRenderInfo.logicPosY) : null;
        const hoverCenterIsVisible = isInRenderRange(mapX, hoverCenterX) && isInRenderRange(mapY, hoverCenterY);
        if (hoverCenterIsVisible) {
          if (hoverRenderInfo.logicPosX !== null && hoverRenderInfo.logicPosY !== null) {
            ctx.fillStyle = 'rgba(255,0,0,0.5)';
            ctx.strokeStyle = 'white';
            ctx.beginPath();
            ctx.arc(hoverCenterX!, hoverCenterY!, 7 * pixelRatio, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
          }
        }
        if (hoverRenderInfo.customHoverRenderer)
          // an optionally provided custom hover render hook
          hoverRenderInfo.customHoverRenderer(hoverInfo, hoverContext, ctx, mapX, mapY, pixelRatioRef.current);
        if (hoverCenterIsVisible) {
          renderHoverLabels(
            canvasOverlayLoc,
            ctx,
            pixelRatio,
            colorSchema,
            hoverCenterX,
            hoverCenterY,
            hoverRenderInfo.labels,
          );
        }
      }
    }
    if (rectDraggingRef.current) {
      ctx.strokeStyle = 'rgba(255,128,0,1)';
      ctx.fillStyle = 'rgba(255,128,0,0.10)';
      const rect = rectDraggingRef.current;
      ctx.setLineDash([3 * pixelRatio, 3 * pixelRatio]);
      ctx.lineWidth = pixelRatio * 1;
      const selectingX = rectDraggingRef.current.selectType == 'x' || rectDraggingRef.current.selectType == 'xy';
      const selectingY = rectDraggingRef.current.selectType == 'y' || rectDraggingRef.current.selectType == 'xy';
      const posX1 = selectingX ? rect.posX1 : -2;
      const posX2 = selectingX ? rect.posX2 : canvasOverlayLoc.width + 2;
      const posY1 = selectingY ? rect.posY1 : -2;
      const posY2 = selectingY ? rect.posY2 : canvasOverlayLoc.height + 2;
      ctx.beginPath();
      ctx.moveTo(posX1, posY1);
      ctx.lineTo(posX2, posY1);
      ctx.lineTo(posX2, posY2);
      ctx.lineTo(posX1, posY2);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    if (lassoDraggingRef.current) {
      ctx.strokeStyle = 'rgba(255,128,0,1)';
      ctx.fillStyle = 'rgba(255,128,0,0.10)';
      ctx.setLineDash([3 * pixelRatio, 3 * pixelRatio]);
      ctx.lineWidth = pixelRatio * 1;
      ctx.beginPath();
      ctx.moveTo(lassoDraggingRef.current.currentPoint.x, lassoDraggingRef.current.currentPoint.y);
      for (const pt of lassoDraggingRef.current.points) ctx.lineTo(pt.x, pt.y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  }

  const debouncedRenderCanvasOverlay = debounceInComponent(renderCanvasOverlay, 25);

  useEffect(() => {
    // This renders the actual canvas
    const pixelRatio = pixelRatioRef.current;
    if (props.widgetContext.setBitmapDownloadFunction) props.widgetContext.setBitmapDownloadFunction(downloadBitmap);
    if (!canvasDraw || !canvasOverlay) return;
    if (!contextDraw) return;
    const style = getComputedStyle(canvasDraw);
    const width = +style.getPropertyValue('width').slice(0, -2);
    const height = +style.getPropertyValue('height').slice(0, -2);
    canvasDraw.width = width * pixelRatio;
    canvasDraw.height = height * pixelRatio;
    canvasOverlay.width = width * pixelRatio;
    canvasOverlay.height = height * pixelRatio;

    // We use throttled, cached rendering for preserving interactivity
    if (imageBuffer[compId]) {
      contextDraw.putImageData(imageBuffer[compId], 0, 0);
    }
    debouncedrenderCanvas();
  });

  useResizeObserver(refLayerDraw, (info: any) => {
    if (!refLayerDraw.current) return;
    const style = getComputedStyle(refLayerDraw.current);
    const dims = `${style.getPropertyValue('width')}x${style.getPropertyValue('height')}`;
    setMeasuredDims(dims);
  });

  const draggingInfoRef = useRef<any>(null);

  const distThreshold = 8 * pixelRatioRef.current;

  const mouseDragHandler = createMouseDragHandler(
    (e: any, posX: number, posY: number) => {
      // Dragging - Mouse Down handler
      const pixelRatio = pixelRatioRef.current;
      const startPosMapping = invMapRenderCoord(
        canvasDraw,
        pixelRatio,
        getZoomPanInfo(),
        propsRef.current!.definition.areas,
        posX * pixelRatio,
        posY * pixelRatio,
      );
      draggingInfoRef.current = {
        startPosX: posX * pixelRatio,
        startPosY: posY * pixelRatio,
        startPosMapping,
        zooming: e.shiftKey || zoomingActive,
        rectangleSelecting: false,
        lassoSelecting: false,
      };
      if (!e.shiftKey && !zoomingActive && propsRef.current!.onRectangleSelection) {
        if (startPosMapping?.area.canAreaSelect)
          draggingInfoRef.current.rectangleSelecting = startPosMapping?.area.canAreaSelect;
      }
      if (!e.shiftKey && !zoomingActive && propsRef.current!.onLassoSelection) {
        if (startPosMapping?.area.canAreaSelect)
          draggingInfoRef.current.lassoSelecting = startPosMapping?.area.canAreaSelect;
      }
    },
    (e: any, posX: number, posY: number, diffX: number, diffY: number) => {
      // Dragging - Mouse Move handler
      const pixelRatio = pixelRatioRef.current;
      if (draggingInfoRef.current) {
        const startPosMapping = draggingInfoRef.current.startPosMapping;
        draggingInfoRef.current.posX = posX * pixelRatio;
        draggingInfoRef.current.posY = posY * pixelRatio;
        if (draggingInfoRef.current.zooming) {
          if (
            fastDist(
              draggingInfoRef.current.posX,
              draggingInfoRef.current.posY,
              draggingInfoRef.current.startPosX,
              draggingInfoRef.current.startPosY,
            ) > distThreshold
          )
            isPanningRef.current = true;
          if (startPosMapping.area.zoomPanX)
            setZoomPanX(calcPan(getZoomPanInfo().x, startPosMapping.mapX, -diffX * pixelRatio));
          if (startPosMapping.area.zoomPanY)
            setZoomPanY(calcPan(getZoomPanInfo().y, startPosMapping.mapY, -diffY * pixelRatio));
          renderCanvas();
          notifyZoomPanChanged();
        }
        if (draggingInfoRef.current.rectangleSelecting) {
          if (
            fastDist(
              draggingInfoRef.current.posX,
              draggingInfoRef.current.posY,
              draggingInfoRef.current.startPosX,
              draggingInfoRef.current.startPosY,
            ) > distThreshold
          )
            rectDraggingRef.current = {
              selectType: draggingInfoRef.current.rectangleSelecting,
              posX1: draggingInfoRef.current.startPosX,
              posY1: draggingInfoRef.current.startPosY,
              posX2: draggingInfoRef.current.posX,
              posY2: draggingInfoRef.current.posY,
            };
          renderCanvasOverlay();
        }
        if (draggingInfoRef.current.lassoSelecting) {
          const currentPoint = { x: draggingInfoRef.current.posX, y: draggingInfoRef.current.posY };
          if (!lassoDraggingRef.current) {
            if (
              fastDist(
                currentPoint.x,
                currentPoint.y,
                draggingInfoRef.current.startPosX,
                draggingInfoRef.current.startPosY,
              ) > distThreshold
            ) {
              lassoDraggingRef.current = {
                points: [{ x: draggingInfoRef.current.startPosX, y: draggingInfoRef.current.startPosY }],
                currentPoint,
              };
            }
          } else {
            lassoDraggingRef.current.currentPoint = currentPoint;
            const lastPoint = lassoDraggingRef.current.points[lassoDraggingRef.current.points.length - 1];
            if (fastDist(lastPoint.x, lastPoint.y, currentPoint.x, currentPoint.y) > 15 * pixelRatio)
              lassoDraggingRef.current.points.push(currentPoint);
          }
          renderCanvasOverlay();
        }
      }
      // if (props.onMouseMove) props.onMouseMove(e, posX, posY, getCoordConvertor(props.xDim, props.yDim, false))
    },
    (e: any) => {
      // Dragging - Mouse Up handler
      const rectDragging = rectDraggingRef.current;
      const lassoDragging = lassoDraggingRef.current;
      const startPosMapping = draggingInfoRef.current.startPosMapping;
      if (draggingInfoRef.current.rectangleSelecting && rectDragging) {
        const lx1 = startPosMapping.mapX.mapInv(rectDragging.posX1);
        const ly1 = startPosMapping.mapY.mapInv(rectDragging.posY1);
        const lx2 = startPosMapping.mapX.mapInv(rectDragging.posX2);
        const ly2 = startPosMapping.mapY.mapInv(rectDragging.posY2);
        props.onRectangleSelection!({
          logicXMin: Math.min(lx1, lx2),
          logicXMax: Math.max(lx1, lx2),
          logicYMin: Math.min(ly1, ly2),
          logicYMax: Math.max(ly1, ly2),
        });
        e.preventDefault();
      }
      if (draggingInfoRef.current.lassoSelecting && lassoDragging) {
        const lassoSelection: TpLassoSelection = {
          logicPoints: lassoDragging.points.map((pt: any) => ({
            x: startPosMapping.mapX.mapInv(pt.x),
            y: startPosMapping.mapY.mapInv(pt.y),
          })),
        };
        lassoSelection.logicPoints.push({
          x: startPosMapping.mapX.mapInv(lassoDragging.currentPoint.x),
          y: startPosMapping.mapY.mapInv(lassoDragging.currentPoint.y),
        });
        props.onLassoSelection!(lassoSelection);
        e.preventDefault();
      }
      if (isPanningRef.current) e.preventDefault();
      draggingInfoRef.current = null;
      setTimeout(() => {
        // we stop the selection state slightly delayed to avoid the mouseclick event opening an item in case the mouse was hovering over it
        rectDraggingRef.current = null;
        lassoDraggingRef.current = null;
        isPanningRef.current = false;
        renderCanvasOverlay();
      }, 50);
    },
  );

  function mouseScrollwheelHandler(ev: any) {
    const pixelRatio = pixelRatioRef.current;
    if (ev.shiftKey || zoomingActiveRef.current) {
      ev.stopPropagation();
      ev.preventDefault();
      const canvas = refLayerDraw.current;
      const bb = refLayerOverlay!.current!.getBoundingClientRect();
      const rs = invMapRenderCoord(
        canvas,
        pixelRatio,
        getZoomPanInfo(),
        propsRef.current!.definition.areas,
        (ev.clientX - bb.left) * pixelRatio,
        (ev.clientY - bb.top) * pixelRatio,
      );
      if (rs) {
        const zoomDiffFactor = Math.exp(-ev.deltaY / 200);
        if (rs.area.zoomPanX) {
          const maxZoomFactor = calcMaxZoomFactor(props.maxZoomXAbsolute, props.maxZoomXRelative, rs.mapX, pixelRatio);
          setZoomPanX(calcZoom(getZoomPanInfo().x, rs.mapX, zoomDiffFactor, rs.logicX, maxZoomFactor));
        }
        if (rs.area.zoomPanY) {
          const maxZoomFactor = calcMaxZoomFactor(props.maxZoomYAbsolute, props.maxZoomYRelative, rs.mapY, pixelRatio);
          setZoomPanY(calcZoom(getZoomPanInfo().y, rs.mapY, zoomDiffFactor, rs.logicY, maxZoomFactor));
        }
        notifyZoomPanChanged();
        renderCanvas();
      }
    }
  }

  const lastHoverInfoStr = useRef<string>('');

  function mouseMoveHandler(ev: any) {
    const pixelRatio = pixelRatioRef.current;
    if (!canvasOverlay) return;
    const bb = refLayerOverlay!.current!.getBoundingClientRect();
    const renderPositX = (ev.clientX - bb.left) * pixelRatio;
    const renderPositY = (ev.clientY - bb.top) * pixelRatio;
    const rs = invMapRenderCoord(
      canvasOverlay,
      pixelRatio,
      getZoomPanInfo(),
      propsRef.current!.definition.areas,
      renderPositX,
      renderPositY,
    );
    let cursor = 'default';
    if (rs) {
      if (rs.area.canAreaSelect) cursor = 'crosshair';
      if (zoomingActiveRef.current) {
        if (rs.area.zoomPanX && rs.area.zoomPanY) cursor = 'move';
        if (!rs.area.zoomPanX && rs.area.zoomPanY) cursor = 'ns-resize';
        if (rs.area.zoomPanX && !rs.area.zoomPanY) cursor = 'ew-resize';
      }
      const hoverInfo = propsRef.current!.getHoverInfo(rs, rs.logicX, rs.logicY, renderPositX, renderPositY);
      if (hoverInfo) {
        hoverItemIdRef.current = hoverInfo.itemId;
        hoverInfo.elemId = elemId;
        if (propsRef.current!.onItemClicked) cursor = 'pointer';
      } else {
        hoverItemIdRef.current = null;
      }
      const hoverInfoStr = JSON.stringify(hoverInfo);
      if (lastHoverInfoStr.current != hoverInfoStr) {
        sendEvent('VisualAnalyticsHover', hoverInfo);
        lastHoverInfoStr.current = hoverInfoStr;
      }
    }
    canvasOverlay.style.cursor = cursor;
  }

  function mouseClickHandler(ev: any) {
    if (
      hoverItemIdRef.current !== null &&
      propsRef.current!.onItemClicked &&
      !rectDraggingRef.current &&
      !lassoDraggingRef.current &&
      !isPanningRef.current
    ) {
      propsRef.current!.onItemClicked(hoverItemIdRef.current);
    }
  }

  useEventNotification(
    'VisualAnalyticsHover',
    (tpe: string, hoverInfo: TpHoverInfo) => {
      hoverInfoRef.current = hoverInfo;
      debouncedRenderCanvasOverlay();
    },
    [],
  );

  useEffect(() => {
    // registers the mouse wheel event handler
    if (refLayerOverlay && refLayerOverlay.current) {
      // Note: we must attach the wheel handler this way because onWheel makes the handler passive, disabling preventDefault
      refLayerOverlay.current.addEventListener('wheel', mouseScrollwheelHandler, { passive: false });
      return () => {
        // on cleanup, unregister
        if (refLayerOverlay && refLayerOverlay.current)
          refLayerOverlay.current.removeEventListener('wheel', mouseScrollwheelHandler);
      };
    }
  }, [
    refLayerOverlay.current,
    props.maxZoomXAbsolute,
    props.maxZoomXRelative,
    props.maxZoomYAbsolute,
    props.maxZoomYRelative,
  ]);

  const webGL2DLayers: any[] = [];
  for (const area of props.definition.areas) {
    if (area.webGL2DRenderData) {
      // In case the area definition renders WebGL, too
      let renderWidth = 1;
      let renderHeight = 1;
      const canvasOverlayLoc = refLayerOverlay.current;
      if (canvasOverlayLoc) {
        renderWidth = canvasOverlayLoc.width;
        renderHeight = canvasOverlayLoc.height;
      }

      const scalers = convertZoomPan2WebGlRangeDual(
        area,
        getZoomPanInfo(),
        renderWidth,
        renderHeight,
        pixelRatioRef.current,
      );
      const scaleX = scalers.scaleX;
      const scaleY = scalers.scaleY;

      webGL2DLayers.push(
        <div
          key={`webgl_${area.id}`}
          style={{ position: 'absolute', ...createAreaPositioningCss(area), backgroundColor: 'transparent' }}
        >
          <CanvasWebGL
            renderData={area.webGL2DRenderData}
            scaleX={scaleX}
            scaleY={scaleY}
            registerContext={(ctx: TpWebGl2DContext) => {
              webGLContexts.current[area.id] = ctx;
            }}
          />
        </div>,
      );
    }
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      <canvas ref={refLayerDraw} style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }} />
      {webGL2DLayers}
      <canvas
        ref={refLayerOverlay}
        style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
        onPointerDownCapture={mouseDragHandler}
        onMouseMove={mouseMoveHandler}
        onMouseLeave={() => {
          hoverInfoRef.current = null;
          lastHoverInfoStr.current = '';
          renderCanvasOverlay();
        }}
        onClick={mouseClickHandler}
      />
    </div>
  );
}
